'use client';

import { useEffect } from 'react';
import { useTranslations } from 'next-intl';

import { AuthPanel } from './AuthPanel';
import { useModalFocusTrap } from './useModalFocusTrap';

interface AuthModalProps {
	mode: 'login' | 'register';
	onClose: () => void;
}

/**
 * Popup wrapper around the full AuthPanel (login, 2FA, passkey).
 * Provides the dimmed/blurred backdrop, Esc + click-outside close, and scroll
 * lock. The panel itself renders in its modal variant via the `onClose` prop.
 */
export function AuthModal({ mode, onClose }: AuthModalProps) {
	const t = useTranslations('Auth');
	const dialogRef = useModalFocusTrap<HTMLDivElement>();

	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') onClose();
		};
		document.addEventListener('keydown', onKey);
		const prevOverflow = document.body.style.overflow;
		document.body.style.overflow = 'hidden';
		return () => {
			document.removeEventListener('keydown', onKey);
			document.body.style.overflow = prevOverflow;
		};
	}, [onClose]);

	return (
		<div
			ref={dialogRef}
			role="dialog"
			aria-modal="true"
			aria-label={t('titleLogin')}
			tabIndex={-1}
			className="fixed inset-0 z-[220] flex items-start justify-center overflow-y-auto bg-black/70 px-4 py-10 backdrop-blur-sm sm:py-16"
		>
			<button
				type="button"
				aria-label={t('close')}
				className="absolute inset-0 cursor-default"
				onClick={onClose}
			/>
			<div className="relative w-full max-w-[980px]">
				<AuthPanel initialMode={mode} onClose={onClose} />
			</div>
		</div>
	);
}
